import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, Target, TrendingUp, Zap, DollarSign, 
  AlertTriangle, CheckCircle, Clock, Building2, Search, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DemandHeatmap = () => {
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('Delhi');
  const [hotspots, setHotspots] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [summary, setSummary] = useState(null);

  // Custom location form
  const [customLat, setCustomLat] = useState('');
  const [customLng, setCustomLng] = useState('');
  const [customRadius, setCustomRadius] = useState('10');

  useEffect(() => {
    loadCities();
    loadSummary();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadHotspots(selectedCity);
    }
  }, [selectedCity]);

  const loadCities = async () => {
    try {
      const response = await fetch(`${API_URL}/api/demand-heatmap/cities`);
      const data = await response.json();
      if (data.success) {
        setCities(data.cities);
      }
    } catch (err) {
      console.error('Failed to load cities:', err);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/demand-heatmap/summary`);
      const data = await response.json();
      if (data.success) {
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  };

  const loadHotspots = async (city) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/demand-heatmap/hotspots?city=${city}&limit=10`);
      const data = await response.json();
      if (data.success) {
        setHotspots(data.hotspots);
      }
    } catch (err) {
      console.error('Failed to load hotspots:', err);
    } finally {
      setLoading(false);
    }
  };

  const analyzeLocation = async () => {
    if (!customLat || !customLng) return;

    try {
      setAnalyzing(true);
      const response = await fetch(
        `${API_URL}/api/demand-heatmap/analyze?lat=${customLat}&lng=${customLng}&radius_km=${customRadius}`
      );
      const data = await response.json();
      if (data.success) {
        setAnalysis(data);
      }
    } catch (err) {
      console.error('Analysis failed:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      'TIER_1_URGENT': 'bg-red-100 text-red-800 border-red-300',
      'TIER_2_HIGH': 'bg-orange-100 text-orange-800 border-orange-300',
      'TIER_3_MEDIUM': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'TIER_4_LOW': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    const labels = {
      'TIER_1_URGENT': 'URGENT',
      'TIER_2_HIGH': 'HIGH',
      'TIER_3_MEDIUM': 'MEDIUM',
      'TIER_4_LOW': 'LOW'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${colors[tier]}`}>
        {labels[tier] || tier}
      </span>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800',
      'HIGH': 'bg-orange-100 text-orange-800',
      'MODERATE': 'bg-yellow-100 text-yellow-800',
      'LOW': 'bg-green-100 text-green-800'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[severity]}`}>
        {severity}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Demand Heatmap</h1>
          <p className="text-gray-600 mt-1">Identify high-potential deployment zones for EV charging stations</p>
        </div>
        <Button onClick={() => loadHotspots(selectedCity)} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-800">Total Analyses</p>
                  <p className="text-3xl font-bold text-purple-900">{summary.summary.total_analyses}</p>
                </div>
                <Target className="w-10 h-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">Avg Opportunity Score</p>
                  <p className="text-3xl font-bold text-green-900">{summary.summary.average_opportunity_score}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-800">High Opportunity Zones</p>
                  <p className="text-3xl font-bold text-orange-900">{summary.summary.high_opportunity_zones}</p>
                </div>
                <Zap className="w-10 h-10 text-orange-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800">Chargers Needed</p>
                  <p className="text-3xl font-bold text-blue-900">{summary.summary.total_chargers_needed}</p>
                </div>
                <Building2 className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: City Selection & Custom Analysis */}
        <div className="space-y-6">
          {/* City Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                City Hotspots
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Select City</Label>
                  <Select value={selectedCity} onValueChange={setSelectedCity}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map(city => (
                        <SelectItem key={city.name} value={city.name}>
                          {city.name}, {city.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custom Location Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5" />
                Custom Location Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Latitude</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      placeholder="28.7041"
                      value={customLat}
                      onChange={(e) => setCustomLat(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Longitude</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      placeholder="77.1025"
                      value={customLng}
                      onChange={(e) => setCustomLng(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Analysis Radius (km)</Label>
                  <Input
                    type="number"
                    placeholder="10"
                    value={customRadius}
                    onChange={(e) => setCustomRadius(e.target.value)}
                  />
                </div>
                <Button 
                  onClick={analyzeLocation} 
                  className="w-full"
                  disabled={analyzing || !customLat || !customLng}
                >
                  {analyzing ? 'Analyzing...' : 'Analyze Location'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Analysis Result */}
          {analysis && (
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle>Location Analysis Result</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Opportunity Score */}
                <div className="text-center py-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Opportunity Score</p>
                  <p className={`text-5xl font-bold ${getScoreColor(analysis.opportunity_score)}`}>
                    {analysis.opportunity_score}
                  </p>
                  <div className="mt-2">
                    {getTierBadge(analysis.priority_tier)}
                  </div>
                </div>

                {/* Gap Analysis */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold text-blue-800 mb-2">Gap Analysis</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">Severity</p>
                      {getSeverityBadge(analysis.gap_analysis.severity)}
                    </div>
                    <div>
                      <p className="text-gray-600">Chargers Needed</p>
                      <p className="font-bold">{analysis.gap_analysis.additional_chargers_needed}</p>
                    </div>
                  </div>
                </div>

                {/* ROI Estimate */}
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm font-semibold text-green-800 mb-2">ROI Estimate</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Setup Cost</span>
                      <span className="font-semibold">{formatCurrency(analysis.roi_estimate.setup_cost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Annual Revenue</span>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(analysis.roi_estimate.estimated_annual_revenue)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payback Period</span>
                      <span className="font-semibold">{analysis.roi_estimate.payback_period_months} months</span>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                {analysis.recommendations.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Recommendations</p>
                    <ul className="space-y-1">
                      {analysis.recommendations.map((rec, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                          <span className="text-purple-500 mt-0.5">→</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Hotspots List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Top Deployment Hotspots - {selectedCity}
                </span>
                <Badge variant="outline">{hotspots.length} Locations</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12 text-gray-500">
                  Analyzing {selectedCity}...
                </div>
              ) : hotspots.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No hotspots found for {selectedCity}
                </div>
              ) : (
                <div className="space-y-4">
                  {hotspots.map((hotspot) => (
                    <Card 
                      key={hotspot.rank} 
                      className="border-l-4"
                      style={{
                        borderLeftColor: 
                          hotspot.opportunity_score >= 80 ? '#16a34a' :
                          hotspot.opportunity_score >= 60 ? '#2563eb' :
                          hotspot.opportunity_score >= 40 ? '#ea580c' : '#dc2626'
                      }}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-lg font-bold text-gray-400">#{hotspot.rank}</span>
                              <span className={`text-2xl font-bold ${getScoreColor(hotspot.opportunity_score)}`}>
                                {hotspot.opportunity_score}
                              </span>
                              {getTierBadge(hotspot.priority_tier)}
                              {getSeverityBadge(hotspot.gap_severity)}
                            </div>

                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                              <MapPin className="w-4 h-4" />
                              <span>
                                {hotspot.location.lat.toFixed(4)}, {hotspot.location.lng.toFixed(4)}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span>Grid: {hotspot.location.grid_id}</span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div className="p-2 bg-gray-50 rounded">
                                <p className="text-gray-500 text-xs">Unmet Demand</p>
                                <p className="font-semibold text-orange-600">{hotspot.unmet_demand.toFixed(1)}/day</p>
                              </div>
                              <div className="p-2 bg-gray-50 rounded">
                                <p className="text-gray-500 text-xs">Chargers Needed</p>
                                <p className="font-semibold text-blue-600">{hotspot.additional_chargers_needed}</p>
                              </div>
                              <div className="p-2 bg-gray-50 rounded">
                                <p className="text-gray-500 text-xs">Est. ROI</p>
                                <p className="font-semibold text-green-600">{hotspot.roi_estimate.expected_roi_percentage.toFixed(0)}%</p>
                              </div>
                              <div className="p-2 bg-gray-50 rounded">
                                <p className="text-gray-500 text-xs">Payback</p>
                                <p className="font-semibold text-purple-600">{hotspot.roi_estimate.payback_period_months.toFixed(0)} mo</p>
                              </div>
                            </div>

                            {hotspot.top_recommendation && (
                              <div className="mt-3 p-2 bg-purple-50 rounded-lg">
                                <p className="text-xs text-purple-800">{hotspot.top_recommendation}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DemandHeatmap;
